/**
 * HardGateGuard — App-level wrapper that blocks ALL protected routes from
 * rendering before authentication when the storefront hard-gate is active.
 *
 * Public/excluded routes (login flows, password reset, owner login) are
 * skipped so users can complete authentication. Everything else is blocked
 * with an immediate redirect to /auth-gate while the auth check resolves.
 *
 * Synchronous pre-paint check: we read the persisted Supabase session key
 * from localStorage before React renders `children`, so a signed-out visitor
 * never sees a flash of the storefront while `useAuth` is still loading.
 */
import { ReactNode, useLayoutEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useStorefrontSettings } from "@/stores/storefrontSettingsStore";
import { setAuthGateReturn } from "@/pages/AuthGate";
import { hasPersistedSession } from "@/lib/softAuthGate";
import { hasGuestPass, clearGuestPass } from "@/lib/guestPass";

const PUBLIC_PREFIXES = [
  "/auth-gate",
  "/reset-password",
  "/owner-access",
  "/unsubscribe",
  "/admin", // admin panel has its own gate; storefront auth-gate must never block it
  "/store-manager/signup",
  "/store/",
];

export default function HardGateGuard({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const sfSettings = useStorefrontSettings();

  // A visitor who chose "Browse as guest / Sign up later" downgrades the hard
  // gate to the soft gate: browsing allowed, checkout still asks for sign-in.
  const guestPass = hasGuestPass();
  const hardGateActive =
    !!sfSettings.storefrontAuthRequired &&
    (sfSettings.storefrontAuthMode || "soft") === "hard" &&
    !guestPass;

  // Once signed in, the guest pass is no longer needed.
  useLayoutEffect(() => {
    if (user && guestPass) clearGuestPass();
  }, [user, guestPass]);

  const path = location.pathname;
  const isPublic = PUBLIC_PREFIXES.some(p => path === p || path.startsWith(p + "/"));

  // Synchronous pre-paint decision: if we're clearly signed out (no persisted
  // session) AND auth is still resolving, treat as blocked so no storefront
  // frame paints. If we DO have a persisted session, allow render while auth
  // hydrates — the odds of a valid session refreshing are high.
  const presumedSignedIn = !!user || hasPersistedSession();
  const blockedNow = hardGateActive && !isPublic && !presumedSignedIn;
  const blockedResolved = hardGateActive && !loading && !user && !isPublic;
  const blocked = blockedNow || blockedResolved;

  useLayoutEffect(() => {
    if (!blocked) return;
    setAuthGateReturn(`${path}${location.search}`);
    const sp = new URLSearchParams(location.search);
    const ref = sp.get("ref");
    navigate(`/auth-gate${ref ? `?ref=${encodeURIComponent(ref)}` : ""}`, { replace: true });
  }, [blocked, navigate, path, location.search]);

  if (blocked) return <div className="min-h-screen bg-background" />;
  return <>{children}</>;
}
